import {
  appActions,
  CustomerType,
  Country,
  selectCountry,
  selectCustomerType,
  selectFeatureFlags,
  selectLoadedFeatureFlags,
  selectLocale,
  Features,
} from '@buy-viasat/redux/src/app';
import { requestAccessToken, selectAccessToken } from '@buy-viasat/redux/src/auth';
import {
  navActions,
  parseQueryString,
  Routes,
  selectCurrentAppRoute,
  selectMaxRoute,
  selectRoutesStates,
} from '@buy-viasat/redux/src/navigator';
import { selectIsServiceAddressSet, serviceabilityActions } from '@buy-viasat/redux/src/serviceability';
import { RESET_STATE_ACTION } from '@buy-viasat/redux/src/types';
import { Product } from '@buy-viasat/types/build/bv';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router-dom';
import useAutoPublishViewChangesToExperimentationPlatform from 'shared/containers/Analytics/hooks/useAutoPublishViewChangesToExperimentationPlatform';
import { persistor } from '../store';
import { BeamFlowNavigator } from './Flows/BeamFlowNavigator';
import { LoadingSpinner, MaintenanceMode } from '@buy-viasat/react';
import { paymentInformationActions } from '@buy-viasat/redux/src/payment-information';
import { planActions } from '@buy-viasat/redux/src/plan';
import { addressActions } from '@buy-viasat/redux/src/address';
import { transformFeatureFlags } from 'shared/utils/transformFeatureFlags';
import { cartActions } from '@buy-viasat/redux/src/cart';
import { ScrubbedAddress, useScrubAddress } from 'shared/utils/dataFetchingHooks/useScrubAddress';

import { USE_NEW_API } from '../../../src/shared/utils/dataFetchingHooks/constants';
import { useGetFeatureFlags } from '../../../src/shared/utils/dataFetchingHooks/useGetFeatureFlags';

const useDefaultLocale = (): string => {
  const { i18n } = useTranslation();
  return i18n.language;
};

const TrackLocale = ({ location }: { location: any }): JSX.Element => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const currentCountry = useSelector(selectCountry);
  const currentLocale = useSelector(selectLocale);

  const validLocale = ((i18n.options?.supportedLngs as string[] | null) ?? ['en-US']).includes(locale);

  if (!validLocale) {
    return <Redirect to={{ ...location, pathname: '/' }} />;
  }

  if (currentLocale !== locale) {
    dispatch(RESET_STATE_ACTION);
    dispatch(appActions.setLocale(locale));
    return <Redirect to={{ ...location, pathname: '/' }} />;
  }

  const [, country] = locale.split('-');

  const validCountry = (country: Country): boolean => Country[country] !== undefined;
  if (validCountry(country as Country) && country !== currentCountry) {
    dispatch(appActions.setCountry(country as Country));
    return <Redirect to={{ ...location, pathname: `/${locale}` }} />;
  } else if (country !== currentCountry) {
    console.error('country does not match store, redirecting home');
    return <Redirect to={{ ...location, pathname: '/' }} />;
  } else if (!validCountry(country as Country)) {
    console.error(`Invalid country: ${country}`);
    return <Redirect to={{ ...location, pathname: '/' }} />;
  }

  return <></>;
};

const TrackCustomerType = ({ location }: { location: any }): JSX.Element => {
  const dispatch = useDispatch();
  const { locale, customerType } = useParams<{ locale: string; customerType: string }>();
  const currentCustomerType = useSelector(selectCustomerType);
  const currentRoute = useSelector(selectCurrentAppRoute);

  const isValidCustomerType = (customerType: string): customerType is CustomerType => {
    return Object.values(CustomerType).includes(customerType as CustomerType);
  };
  const hasCustomerPassedPlansPage = (currentRoute: string): boolean => {
    return currentRoute !== Routes.SERVICEABILITY && currentRoute !== Routes.PLAN;
  };

  if (!isValidCustomerType(customerType)) {
    return <Redirect to={{ ...location, pathname: `/${locale}` }} />;
  }

  if (currentCustomerType !== customerType) {
    //if a user changes between SMB and Res via the Browser Back button, re-fetch plans
    dispatch(planActions.setHavePlansLoaded(false));
    dispatch(planActions.resetSelectedPlan());
    dispatch(navActions.invalidateProgressFrom(Routes.PLAN));
    dispatch(
      cartActions.setCartItems({
        plan: {},
        addons: [],
      }),
    );
    if (hasCustomerPassedPlansPage(currentRoute)) {
      //the customer has switched from SMB to Res or vice-versa manually via the URL
      //despite their cunning, nevertheless the customer must restart the purchase flow from the beginning
      return <Redirect to={{ ...location, pathname: `/${locale}/${customerType}/pln` }} />;
    }

    dispatch(appActions.resetPartyId());
    dispatch(paymentInformationActions.clearBillingData());
    dispatch(appActions.setCustomerType(customerType));
    dispatch(planActions.setAvailableProducts([]));
    return <Redirect to={{ ...location, pathname: `/${locale}/${customerType}/` }} />;
  }

  return <></>;
};

export const TrackCurrentStep = ({ location }: { location: ReturnType<typeof useLocation> }): JSX.Element => {
  const routeStates = useSelector(selectRoutesStates);
  const maxRoute = useSelector(selectMaxRoute);
  const locale = useSelector(selectLocale);
  const customerType = useSelector(selectCustomerType);
  const { pathname } = useLocation();
  // Send the user back to their furthest step unless the URL is one they already reached in their flow.
  const pathArr = pathname.split('/');
  const urlRoute = pathArr[3];
  const urlState = routeStates?.[urlRoute as Routes];

  if (urlState) {
    const isReachable = urlState.order >= 0 && urlState.order <= routeStates[maxRoute].order;

    if (!isReachable) {
      return <Redirect to={{ ...location, pathname: `/${locale}/${customerType}/${maxRoute}` }} />;
    }
  }

  return <></>;
};

export const Navigator = (): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const accessToken = useSelector(selectAccessToken);
  const loadedFeatureFlags = useSelector(selectLoadedFeatureFlags);
  const customerType = useSelector(selectCustomerType);

  const country = useSelector(selectCountry);
  const isServiceAddressSet = useSelector(selectIsServiceAddressSet);

  useEffect(() => {
    if (!accessToken) {
      dispatch(
        requestAccessToken({
          env: process.env['REACT_APP_ENV']!,
          authUrl: process.env['REACT_APP_GRAPHQL_API_URL'] + '/auth',
        }),
      );
    }
    if (!USE_NEW_API && !loadedFeatureFlags && accessToken) {
      dispatch(appActions.retrieveFeatureFlags());
    }
  }, [accessToken, loadedFeatureFlags, dispatch]);

  const { data: featureFlagsFromNewApi } = useGetFeatureFlags();
  const featureFlags = USE_NEW_API ? (featureFlagsFromNewApi as Features) : useSelector(selectFeatureFlags);

  useEffect(() => {
    // reset any offer related data on serviceability reload, before parsing query strings
    dispatch(appActions.setOfferIdParam(''));
  }, []);

  const { scrubAddress } = useScrubAddress<ScrubbedAddress>();

  useEffect(() => {
    if (accessToken && location.search) {
      // pass new scrub address function as param
      parseQueryString(dispatch, location.search, isServiceAddressSet, undefined, scrubAddress);
    }
    dispatch(appActions.startSession(persistor));

    if (accessToken) {
      dispatch(appActions.setCidPhoneMap());
    }
  }, [accessToken, location.search]);

  useEffect(() => {
    // set route based on URL
    const currentRoute = location.pathname.split('/')[3];
    dispatch(navActions.setCurrentAppRoute(currentRoute as Routes));
    dispatch(navActions.setupAppRoutes());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (window.location?.pathname?.includes(country) && featureFlags) {
      dispatch(serviceabilityActions.setServiceAddressCountryCode(country)); //serviceAddress
      dispatch(serviceabilityActions.setScrubLocationCountryCode(country)); //scrubAddress
      dispatch(addressActions.setShippingCountryCode(country)); //shippingAddress
      dispatch(addressActions.setBillingCountryCode(country)); //billingAddress
      dispatch(appActions.setCountry(country)); //app
      dispatch(appActions.loadFeatureFlags(transformFeatureFlags(featureFlags)));
    }
  }, [country, customerType, location.pathname]);

  // Setup analytics.
  useAutoPublishViewChangesToExperimentationPlatform();

  const onPlanEdit = () => {
    dispatch(serviceabilityActions.resetServiceAddress());
    dispatch(navActions.routeUserTo(Routes.SERVICEABILITY));
  };

  const onPlanSelect = (product: Product) => {
    const isRegulated = product.characteristics.isRegulated === 'true';

    dispatch(
      planActions.planOnNext({
        isRegulated,
        id: product.id,
        name: product.name,
        planPrice: product.price,
        offerId: product.offerId || '',
        planPromo: product.promo?.price || 0,
        isCafII: product.isCafII || false,
        dataCap: product.characteristics.dataCap || '',
        extensionProducts: product.extensionTypes as string[],
        productFamily: product.characteristics.productFamily || '',
        status: product.characteristics.status || '',
        inflectionPointText: product.characteristics.inflectionPointText || '',
        customerType,
      }),
    );
  };

  const onPlanDetails = () => {
    dispatch(navActions.routeUserTo(Routes.PLAN_DETAILS));
  };

  const defaultLocale = useDefaultLocale(); // Will be browser language or default to en-US

  const hasLoadedFeatureFlags = USE_NEW_API ? !!featureFlagsFromNewApi : loadedFeatureFlags;

  return (
    <Switch>
      <Redirect exact from="/" to={{ ...location, pathname: `/${defaultLocale}` }} />
      <Redirect
        exact
        from={`/${CustomerType.R}`}
        to={{ ...location, pathname: `/${defaultLocale}/${CustomerType.R}/` }}
      />
      <Redirect
        exact
        from={`/${CustomerType.B}`}
        to={{ ...location, pathname: `/${defaultLocale}/${CustomerType.B}/` }}
      />
      <Redirect exact from="/:locale" to={{ ...location, pathname: `/:locale/${CustomerType.R}/` }} />
      <Route
        path="/:locale/:customerType"
        render={({ match: { url: baseURL } }): JSX.Element => {
          return (
            <>
              {!hasLoadedFeatureFlags && <LoadingSpinner my="112px" size="large" />}
              <TrackLocale location={location} />
              <TrackCustomerType location={location} />
              <TrackCurrentStep location={location} />

              {hasLoadedFeatureFlags &&
                (featureFlags.maintenanceMode ? (
                  <MaintenanceMode />
                ) : (
                  <BeamFlowNavigator
                    baseURL={baseURL}
                    onPlanEdit={onPlanEdit}
                    onPlanDetails={onPlanDetails}
                    onPlanSelect={onPlanSelect}
                  />
                ))}
            </>
          );
        }}
      />
      <Redirect to={{ ...location, pathname: `/${defaultLocale}` }} />
    </Switch>
  );
};
